/**
 * The one AudioContext dictation captures through. Browsers (iOS Safari above all) start a
 * context suspended and let it run only when resumed inside a user gesture, so the mic
 * button calls `unlockAudio` from its click, and `ensureRunning` never waits on `resume()`
 * for long: on iOS that promise can stay pending until a tap.
 */

type AudioContextCtor = typeof AudioContext;

let context: AudioContext | null = null;

const Ctor = (): AudioContextCtor | undefined =>
  typeof window === "undefined"
    ? undefined
    : (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext);

/** The shared context, made on first use. Null where the browser has none. */
export function audioContext(): AudioContext | null {
  if (context && context.state !== "closed") return context;
  const Make = Ctor();
  if (!Make) return null;
  context = new Make();
  return context;
}

/** Call from inside a user gesture: resumes the context so capture can start. */
export function unlockAudio(): void {
  const ctx = audioContext();
  if (ctx && ctx.state !== "running") void ctx.resume().catch(() => undefined);
}

/**
 * The context, running, or null when it will not run without a tap. Waits at most
 * `timeoutMs` for `resume()`, which on iOS can stay pending until one.
 */
export async function ensureRunning(timeoutMs = 400): Promise<AudioContext | null> {
  const ctx = audioContext();
  if (!ctx) return null;
  if (ctx.state !== "running") {
    await Promise.race([ctx.resume().catch(() => undefined), new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
  }
  return ctx.state === "running" ? ctx : null;
}
